import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Avatar from "../../../ui/avatar/Avatar";
import MessageContent from "../../mailContent/MessageContent";
import MessageReaderModal from "./MessageReaderModal";
import styles from "./CommunicationDetailPanel.module.css";

const formatDateTime = (date) => {
  if (!date) return "-";

  return new Date(date).toLocaleString("en-US", {
    month: "long",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const stripHtml = (html = "") =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const toRecipientList = (recipients) =>
  Array.isArray(recipients)
    ? recipients.filter(Boolean)
    : String(recipients || "")
        .split(/[,;]/)
        .map((recipient) => recipient.trim())
        .filter(Boolean);

const formatRecipients = (recipients) => toRecipientList(recipients).join(", ");

const getMessageAttachments = (message) =>
  Array.isArray(message?.attachments) ? message.attachments : [];

const getListedAttachments = (attachments) =>
  attachments.filter((attachment) => !attachment.embedded_in_body);

const getMessageId = (message, index) =>
  `${message?.type || "message"}-${message?.id || message?.message_id || index}`;

const getSenderName = (message, recipient) =>
  message?.person?.name ||
  message?.from_name ||
  message?.from_email ||
  recipient?.name ||
  "Sin remitente";

const CAMPAIGN_MAIL_TYPE = "campaign_mail";

export const DOUBLE_CLICK_WINDOW_MS = 220;

const preventDoubleClickSelection = (event) => {
  if (event.detail > 1) event.preventDefault();
};

const MessageThread = ({
  messages = [],
  recipient,
  onSelectMessage,
  onOpenMessageChange,
}) => {
  const [openedMessageId, setOpenedMessageId] = useState(null);
  const [readerMessageId, setReaderMessageId] = useState(null);
  const closeReader = useCallback(() => setReaderMessageId(null), []);
  const openedMessageIdRef = useRef(openedMessageId);
  openedMessageIdRef.current = openedMessageId;
  const pendingToggleRef = useRef(null);

  useEffect(() => () => clearTimeout(pendingToggleRef.current), []);

  const sortedMessages = useMemo(() => {
    return [...messages].sort((a, b) => {
      const campaignOriginOrder =
        Number(b?.type === CAMPAIGN_MAIL_TYPE) - Number(a?.type === CAMPAIGN_MAIL_TYPE);
      if (campaignOriginOrder !== 0) return campaignOriginOrder;
      const dateA = new Date(a?.date || 0);
      const dateB = new Date(b?.date || 0);
      return dateA - dateB;
    });
  }, [messages]);

  const openedMessageExists = sortedMessages.some(
    (message, index) => getMessageId(message, index) === openedMessageId,
  );

  useEffect(() => {
    onOpenMessageChange?.(openedMessageExists);
  }, [openedMessageExists, onOpenMessageChange]);

  const selectInboundMessage = (message) => {
    if (message?.direction === "inbound") {
      onSelectMessage?.(message);
    }
  };

  const cancelPendingToggle = () => {
    clearTimeout(pendingToggleRef.current);
    pendingToggleRef.current = null;
  };

  const toggleMessage = (message, messageId) => {
    const isAlreadyOpen = openedMessageIdRef.current === messageId;
    setOpenedMessageId(isAlreadyOpen ? null : messageId);

    if (!isAlreadyOpen) {
      selectInboundMessage(message);
    }
  };

  const handleToggleMessage = (message, messageId) => {
    cancelPendingToggle();
    pendingToggleRef.current = setTimeout(() => {
      pendingToggleRef.current = null;
      toggleMessage(message, messageId);
    }, DOUBLE_CLICK_WINDOW_MS);
  };

  const handleOpenReader = (message, messageId) => {
    cancelPendingToggle();
    setOpenedMessageId(messageId);
    setReaderMessageId(messageId);
    selectInboundMessage(message);
  };

  if (!sortedMessages.length) {
    return (
      <div className={styles.emptyState}>
        No hay mensajes para mostrar.
      </div>
    );
  }

  const readerIndex = sortedMessages.findIndex(
    (message, index) => getMessageId(message, index) === readerMessageId,
  );
  const readerMessage = readerIndex >= 0 ? sortedMessages[readerIndex] : null;

  return (
    <div className={styles.messageThread}>
      <div className={styles.messageList}>
        {sortedMessages.map((message, index) => {
          const isOutbound = message.direction === "outbound";
          const messageId = getMessageId(message, index);
          const isOpen = openedMessageId === messageId;
          const senderName = getSenderName(message, recipient);
          const senderPhoto = message?.person?.photo;

          const preview =
            stripHtml(message?.body || message?.raw_body || "") ||
            "Sin contenido";

          const toText = formatRecipients(message?.to);
          const ccText = formatRecipients(message?.cc);
          const ccCount = toRecipientList(message?.cc).length;
          const listedAttachmentsCount = getListedAttachments(
            getMessageAttachments(message),
          ).length;

          return (
            <div
              key={messageId}
              className={`${styles.messageCard} ${
                isOutbound ? styles.messageCardOutgoing : ""
              } ${isOpen ? styles.messageCardOpen : ""}`}
            >
              <div className={styles.messageTopRow}>
                <button
                  type="button"
                  className={styles.messageToggle}
                  aria-expanded={isOpen}
                  aria-label={`${isOpen ? "Contraer" : "Expandir"} mensaje de ${senderName}`}
                  title="Doble click para abrirlo en una ventana"
                  onMouseDown={preventDoubleClickSelection}
                  onClick={() => handleToggleMessage(message, messageId)}
                  onDoubleClick={() => handleOpenReader(message, messageId)}
                >
                  <div className={styles.messageContent}>
                    <Avatar
                      src={senderPhoto}
                      name={senderName}
                      size={40}
                    />

                    <div className={styles.messageInfo}>
                      <div className={styles.messageHeader}>
                        <span className={styles.senderLine}>
                          <span className={styles.senderName}>{senderName}</span>
                          {!isOpen && ccCount > 0 && (
                            <span className={styles.messageCcHint} title={ccText}>
                              +{ccCount} en copia
                            </span>
                          )}
                        </span>
                        <span className={styles.messageDate}>
                          {formatDateTime(message?.date)}
                        </span>
                      </div>

                      {isOpen ? (
                        <div className={styles.messageExpandedContent}>
                          <div className={styles.emailSubject}>
                            {message.subject || "Sin asunto"}
                          </div>
                          {(toText || ccText) && (
                            <div className={styles.emailParticipants}>
                              {toText && (
                                <span className={styles.emailParticipant}>
                                  To: {toText}
                                </span>
                              )}
                              {ccText && (
                                <span className={styles.emailParticipant}>
                                  CC: {ccText}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className={styles.messagePreview}>{preview}</p>
                      )}
                    </div>
                  </div>
                </button>

                <div className={styles.messageActions}>
                  {listedAttachmentsCount > 0 && (
                    <span
                      className={styles.messageAttachmentsCount}
                      aria-label={`${listedAttachmentsCount} ${
                        listedAttachmentsCount === 1 ? "adjunto" : "adjuntos"
                      }`}
                    >
                      📎 {listedAttachmentsCount}
                    </span>
                  )}
                  <button
                    type="button"
                    className={styles.messageExpandButton}
                    onClick={() => handleOpenReader(message, messageId)}
                    aria-label={`Abrir en ventana el mensaje de ${senderName}`}
                    title="Abrir en ventana"
                  >
                    <span aria-hidden="true">⤢</span>
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className={styles.messageOpenedBody}>
                  <MessageContent message={message} bodyClassName={styles.emailBody} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {readerMessage && (
        <MessageReaderModal
          message={readerMessage}
          senderName={getSenderName(readerMessage, recipient)}
          senderPhoto={readerMessage?.person?.photo}
          dateLabel={formatDateTime(readerMessage?.date)}
          toText={formatRecipients(readerMessage?.to)}
          ccText={formatRecipients(readerMessage?.cc)}
          onClose={closeReader}
        />
      )}
    </div>
  );
};

export default MessageThread;
